import { Component, OnDestroy, OnInit, inject, input } from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { PayeeAutocompleteComponent } from '../../payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { PayeeFormFieldComponent } from '../../payee-form-fields/payee-form-field/payee-form-field.component';
import { ChipDropdownComponent } from '../chip-dropdown/chip-dropdown.component';
import { ClientOption, UNKNOWN_CLIENT_NAME } from '../types';
import { PayeeCasesService } from '../payee-cases.service';

@Component({
  selector: 'payee-case-client-filter',
  templateUrl: './case-client-filter.component.html',
  styleUrls: ['./case-client-filter.component.scss'],
  imports: [
    ChipDropdownComponent,
    PayeeFormFieldComponent,
    PayeeAutocompleteComponent,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
})
export class CaseClientFilterComponent implements OnInit, OnDestroy {
  private payeeCasesService = inject(PayeeCasesService);
  private readonly destroy = new Subject<void>();

  readonly searchClients = input.required<
    (name: string) => Promise<ClientOption[]>
  >();
  readonly resolveClient = input.required<
    (uuid: string) => Promise<ClientOption | null>
  >();

  clientOptions: ClientOption[] = [];
  readonly clientControl = new FormControl<ClientOption | null>(null);

  dropdownOpen = false;

  get client() {
    return this.payeeCasesService.client.value;
  }

  async ngOnInit() {
    this.payeeCasesService.client
      .pipe(takeUntil(this.destroy))
      .subscribe((client) => {
        this.clientControl.setValue(client, { emitEvent: false });
      });

    this.clientControl.valueChanges
      .pipe(takeUntil(this.destroy))
      .subscribe((client) => {
        this.payeeCasesService.client.next(client);
        if (client) {
          this.dropdownOpen = false;
        }
      });

    if (this.client?.name === UNKNOWN_CLIENT_NAME) {
      try {
        const client = await this.resolveClient()(this.client.uuid);
        if (client) {
          this.payeeCasesService.client.next(client);
        }
      } catch {
        //
      }
    }

    await this.onSearchChange('');
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  displayWith(client?: ClientOption): string {
    return client?.name || '';
  }

  clearFilter(): void {
    this.clientControl.setValue(null);
  }

  async onSearchChange(name?: string): Promise<void> {
    const query = name ?? '';
    if (query.length > 0 && query.length < 3) {
      return;
    }

    try {
      this.clientOptions = await this.searchClients()(query);

      if (this.client?.name === UNKNOWN_CLIENT_NAME) {
        const state = this.clientOptions.find(
          (v) => v.uuid === this.client?.uuid,
        );
        if (state) {
          this.clientControl.setValue(state);
        }
      }
    } catch (error) {
      console.error('Error while searching clients', error);
    }
  }
}
